// Camp Email Service
// Database operations for extracted camp email data

import { supabase, handleSupabaseError } from '@/lib/supabase';

/**
 * Camp Email Service Class
 * Handles database operations for extracted camp email data
 */
class CampEmailService {
  /**
   * Get camp emails for a user with optional filters
   * @param {string} userId - User ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} Array of camp emails
   */
  async getCampEmails(userId, filters = {}) {
    try {
      console.log('🔍 CampEmailService: getCampEmails called with userId:', userId, 'filters:', filters);
      let query = supabase
        .from('user_camp_emails')
        .select('*')
        .eq('user_id', userId);

      // Filter out archived emails by default (unless explicitly included)
      if (filters.includeArchived !== true) {
        query = query.eq('is_archived', false);
      }

      // Apply filters
      if (filters.status) {
        query = query.eq('user_status', filters.status);
      }

      if (filters.needsReview !== undefined) {
        query = query.eq('needs_manual_review', filters.needsReview);
      }

      if (filters.dateRange) {
        if (filters.dateRange.start) {
          query = query.gte('email_date', filters.dateRange.start.toISOString());
        }
        if (filters.dateRange.end) {
          query = query.lte('email_date', filters.dateRange.end.toISOString());
        }
      }

      // Apply sorting - map old column names to new ones
      let sortBy = filters.sortBy || 'email_date';
      if (sortBy === 'email_date') sortBy = 'email_date'; // This matches
      if (sortBy === 'camp_dates_start') sortBy = 'camp_start_date';
      if (sortBy === 'camp_dates_end') sortBy = 'camp_end_date';
      if (sortBy === 'extraction_confidence') sortBy = 'confidence_score';
      
      const sortOrder = filters.sortOrder || 'desc';
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      if (filters.page && filters.pageSize) {
        const from = (filters.page - 1) * filters.pageSize;
        const to = from + filters.pageSize - 1;
        query = query.range(from, to);
      }

      const { data, error } = await query;

      console.log('🔍 CampEmailService: Query result - data:', data, 'error:', error);

      if (error) {
        console.error('🔍 CampEmailService: Database error:', error);
        throw handleSupabaseError(error);
      }

      console.log('🔍 CampEmailService: Returning data:', data || []);
      return data || [];
    } catch (error) {
      console.error('Error fetching camp emails:', error);
      throw error;
    }
  }

  /**
   * Get camp email by ID
   * @param {string} campEmailId - Camp email ID
   * @returns {Promise<Object|null>} Camp email data
   */
  async getCampEmailById(campEmailId) {
    try {
      const { data, error } = await supabase
        .from('user_camp_emails')
        .select('*')
        .eq('id', campEmailId)
        .single();

      if (error) {
        throw handleSupabaseError(error);
      }

      return data;
    } catch (error) {
      console.error('Error fetching camp email by ID:', error);
      throw error;
    }
  }

  /**
   * Update camp email status
   * @param {string} campEmailId - Camp email ID
   * @param {string} status - New status
   * @param {string} notes - Optional notes
   * @returns {Promise<boolean>} Success status
   */
  async updateCampStatus(campEmailId, status, notes = null) {
    try {
      const updates = {
        user_status: status,
        updated_at: new Date().toISOString()
      };

      if (notes !== null) {
        updates.user_notes = notes;
      }

      const { error } = await supabase
        .from('user_camp_emails')
        .update(updates)
        .eq('id', campEmailId);

      if (error) {
        throw handleSupabaseError(error);
      }

      return true;
    } catch (error) {
      console.error('Error updating camp status:', error);
      throw error;
    }
  }

  /**
   * Bulk update camp email status
   * @param {Array<string>} campEmailIds - Array of camp email IDs
   * @param {string} status - New status
   * @returns {Promise<boolean>} Success status
   */
  async bulkUpdateCampStatus(campEmailIds, status) {
    try {
      const { error } = await supabase
        .from('user_camp_emails')
        .update({
          user_status: status,
          updated_at: new Date().toISOString()
        })
        .in('id', campEmailIds);

      if (error) {
        throw handleSupabaseError(error);
      }

      return true;
    } catch (error) {
      console.error('Error bulk updating camp status:', error);
      throw error;
    }
  }

  /**
   * Search camp emails
   * @param {string} userId - User ID
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Array of matching camp emails
   */
  async searchCampEmails(userId, searchTerm) {
    try {
      const { data, error } = await supabase
        .from('user_camp_emails')
        .select('*')
        .eq('user_id', userId)
        .eq('is_archived', false)
        .or(`camp_name.ilike.%${searchTerm}%,camp_organization.ilike.%${searchTerm}%,camp_address.ilike.%${searchTerm}%,subject.ilike.%${searchTerm}%`)
        .order('email_date', { ascending: false });

      if (error) {
        throw handleSupabaseError(error);
      }

      return data || [];
    } catch (error) {
      console.error('Error searching camp emails:', error);
      throw error;
    }
  }

  /**
   * Get camp email statistics
   * Optimized to use multiple targeted queries instead of fetching all data
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Statistics object
   */
  async getCampEmailStats(userId) {
    try {
      // Try RPC first (single query instead of 7 HEAD requests)
      const { data: counts, error: rpcError } = await supabase
        .rpc('get_email_segment_counts', { user_uuid: userId })
      
      if (!rpcError && counts) {
        // Map RPC response to expected format
        return {
          total: counts.total || 0,
          new: counts.new_pending || 0,
          favorited: counts.favorited || 0,
          hidden: counts.hidden || 0,
          savedForLater: 0, // RPC doesn't include this, will be 0 or can be added to RPC later
          reviewed: counts.reviewed || 0,
          needsReview: counts.needs_review || 0
        }
      }
      
      // Fallback to old method if RPC doesn't exist (error code 42883 = function does not exist)
      if (rpcError && rpcError.code !== '42883') {
        console.warn('RPC error (non-fatal), falling back to HEAD requests:', rpcError)
      } else if (rpcError) {
        console.log('RPC function not available yet, using HEAD requests fallback')
      }
      
      // Use parallel queries with count filters to minimize data transfer
      // Using head: true returns only count, no data
      const [totalResult, newResult, favoritedResult, hiddenResult, savedResult, reviewedResult, needsReviewResult] = await Promise.all([
        // Total count (excluding archived)
        supabase
          .from('user_camp_emails')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_archived', false),
        
        // New/Pending count (excluding archived)
        supabase
          .from('user_camp_emails')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_archived', false)
          .in('user_status', ['new', 'pending']),
        
        // Favorited count (excluding archived)
        supabase
          .from('user_camp_emails')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_archived', false)
          .eq('user_status', 'favorited'),
        
        // Hidden count (excluding archived)
        supabase
          .from('user_camp_emails')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_archived', false)
          .eq('user_status', 'hidden'),
        
        // Saved for later count (excluding archived)
        supabase
          .from('user_camp_emails')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_archived', false)
          .eq('user_status', 'saved_for_later'),
        
        // Reviewed count (excluding archived)
        supabase
          .from('user_camp_emails')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_archived', false)
          .eq('user_status', 'reviewed'),
        
        // Needs review count (excluding archived)
        supabase
          .from('user_camp_emails')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_archived', false)
          .eq('needs_manual_review', true)
      ]);

      // Check for errors
      const results = [totalResult, newResult, favoritedResult, hiddenResult, savedResult, reviewedResult, needsReviewResult];
      const error = results.find(r => r.error)?.error;
      
      if (error) {
        throw handleSupabaseError(error);
      }

      const stats = {
        total: totalResult.count || 0,
        new: newResult.count || 0,
        favorited: favoritedResult.count || 0,
        hidden: hiddenResult.count || 0,
        savedForLater: savedResult.count || 0,
        reviewed: reviewedResult.count || 0,
        needsReview: needsReviewResult.count || 0
      };

      return stats;
    } catch (error) {
      console.error('Error fetching camp email stats:', error);
      throw error;
    }
  }

  /**
   * Update extracted camp data (for manual review)
   * @param {string} campEmailId - Camp email ID
   * @param {Object} updates - Data updates
   * @returns {Promise<boolean>} Success status
   */
  async updateExtractedData(campEmailId, updates) {
    try {
      const { error } = await supabase
        .from('user_camp_emails')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
          needs_manual_review: false // Mark as reviewed after manual update
        })
        .eq('id', campEmailId);

      if (error) {
        throw handleSupabaseError(error);
      }

      return true;
    } catch (error) {
      console.error('Error updating extracted data:', error);
      throw error;
    }
  }

  /**
   * Mark camp as reviewed
   * @param {string} campEmailId - Camp email ID
   * @returns {Promise<boolean>} Success status
   */
  async markAsReviewed(campEmailId) {
    try {
      const { error } = await supabase
        .from('user_camp_emails')
        .update({
          user_status: 'reviewed',
          needs_manual_review: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', campEmailId);

      if (error) {
        throw handleSupabaseError(error);
      }

      return true;
    } catch (error) {
      console.error('Error marking camp as reviewed:', error);
      throw error;
    }
  }

  /**
   * Delete camp email
   * @param {string} campEmailId - Camp email ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteCampEmail(campEmailId) {
    try {
      const { error } = await supabase
        .from('user_camp_emails')
        .delete()
        .eq('id', campEmailId);

      if (error) {
        throw handleSupabaseError(error);
      }

      return true;
    } catch (error) {
      console.error('Error deleting camp email:', error);
      throw error;
    }
  }

  /**
   * Get camps needing manual review
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of camps needing review
   */
  async getCampsNeedingReview(userId) {
    try {
      const { data, error } = await supabase
        .from('user_camp_emails')
        .select('*')
        .eq('user_id', userId)
        .eq('is_archived', false)
        .eq('needs_manual_review', true)
        .order('extraction_confidence', { ascending: true }); // Lowest confidence first

      if (error) {
        throw handleSupabaseError(error);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching camps needing review:', error);
      throw error;
    }
  }

  /**
   * Get recent camp emails
   * @param {string} userId - User ID
   * @param {number} limit - Number of recent emails to fetch
   * @returns {Promise<Array>} Array of recent camp emails
   */
  async getRecentCampEmails(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('user_camp_emails')
        .select('*')
        .eq('user_id', userId)
        .eq('is_archived', false)
        .order('email_date', { ascending: false })
        .limit(limit);

      if (error) {
        throw handleSupabaseError(error);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching recent camp emails:', error);
      throw error;
    }
  }
}

// Create singleton instance
const campEmailService = new CampEmailService();

// Export individual functions for convenience
export const getCampEmails = (userId, filters) => 
  campEmailService.getCampEmails(userId, filters);

export const getCampEmailById = (campEmailId) => 
  campEmailService.getCampEmailById(campEmailId);

export const updateCampStatus = (campEmailId, status, notes) => 
  campEmailService.updateCampStatus(campEmailId, status, notes);

export const bulkUpdateCampStatus = (campEmailIds, status) => 
  campEmailService.bulkUpdateCampStatus(campEmailIds, status);

export const searchCampEmails = (userId, searchTerm) => 
  campEmailService.searchCampEmails(userId, searchTerm);

export const getCampEmailStats = (userId) => 
  campEmailService.getCampEmailStats(userId);

export const updateExtractedData = (campEmailId, updates) => 
  campEmailService.updateExtractedData(campEmailId, updates);

export const markAsReviewed = (campEmailId) => 
  campEmailService.markAsReviewed(campEmailId);

export const deleteCampEmail = (campEmailId) => 
  campEmailService.deleteCampEmail(campEmailId);

export const getCampsNeedingReview = (userId) => 
  campEmailService.getCampsNeedingReview(userId);

export const getRecentCampEmails = (userId, limit) => 
  campEmailService.getRecentCampEmails(userId, limit);

export default campEmailService;
